import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Filter, X, LogOut, User, Settings, LogIn, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  // Button visibility props
  showFilterButton?: boolean;
  showExportButton?: boolean;
  // Filter props
  categories?: string[];
  priorities?: string[];
  selectedCategory?: string;
  selectedPriority?: string;
  onCategoryChange?: (category: string) => void;
  onPriorityChange?: (priority: string) => void;
  onResetFilters?: () => void;
  filteredCount?: number;
  totalCount?: number;
  // Date range props
  dateRange?: { startDate: string; endDate: string };
  onDateRangeChange?: (dateRange: { startDate: string; endDate: string }) => void;
  // Export props
  onExport?: (format: 'csv' | 'excel' | 'pdf') => void;
  exportLoading?: boolean;
  exportError?: string | null;
}

const Header: React.FC<HeaderProps> = ({ 
  title = "CLIA Laboratory Analytics", 
  showBackButton = false, 
  onBack,
  // Button visibility props
  showFilterButton = false,
  showExportButton = false,
  // Filter props
  categories = [],
  priorities = [],
  selectedCategory = 'all',
  selectedPriority = 'all',
  onCategoryChange,
  onPriorityChange,
  onResetFilters,
  filteredCount = 0,
  totalCount = 0,
  // Date range props
  dateRange = { startDate: '', endDate: '' },
  onDateRangeChange,
  // Export props
  onExport,
  exportLoading = false,
  exportError = null
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPortalUser = user?.email?.toLowerCase() === 'portal@clinicalsquared.com';
  // The on-prem CCPPortal URL is injected at build time via env var.
  // Only used to render the "Return to Portal" button for portal SSO users.
  // If the env var is unset (e.g., local dev), the button is hidden.
  const portalUrl = process.env.REACT_APP_PORTAL_URL;
  const exportRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Close export panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setShowExport(false);
      }
    };

    if (showExport) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExport]);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);
  return (
    <motion.header 
      className="header"
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="header-content">
        <div className="header-left">
          {showBackButton && (
            <motion.button 
              className="back-button"
              onClick={onBack}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft size={20} />
              Back to Dashboard
            </motion.button>
          )}
          <div className="header-title">
            <h1>{title}</h1>
            <p>Advanced Cytology/Pathology Laboratory Compliance Platform</p>
          </div>
          {/* Return to Portal button — only shown when the portal SSO user
              is active and the on-prem CCPPortal URL is configured at build
              time. Clicking it sends the browser back to the portal home
              page. The SSO session here remains active in case the user
              comes back. */}
          {isPortalUser && portalUrl && (
            <motion.button
              className="action-button return-to-portal-button"
              onClick={() => { window.location.href = portalUrl; }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ExternalLink size={18} />
              Return to Portal
            </motion.button>
          )}
          {/* Show a Sign In button when the portal SSO user is active so
              they can switch to a personal account with full permissions
              (sign-off, admin, etc.). Clicking it clears the SSO session
              and redirects to the normal login page. */}
          {isPortalUser && (
            <motion.button
              className="action-button sign-in-button"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogIn size={18} />
              Sign In
            </motion.button>
          )}
        </div>

        <div className="header-right">
          <div className="header-actions">
            {showFilterButton && (
              <div className="filter-button-container" ref={filterRef}>
                <motion.button 
                  className={`action-button ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Filter size={18} />
                  Filter
                  {selectedCategory !== 'all' || selectedPriority !== 'all' ? (
                    <span className="badge">{filteredCount}</span>
                  ) : null}
                </motion.button>
                
                {showFilters && (
                  <motion.div 
                    className="header-dropdown filters-panel"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="dropdown-header">
                      <h3>Filters</h3>
                      <button 
                        className="close-btn"
                        onClick={() => setShowFilters(false)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    <div className="filter-group">
                      <label htmlFor="category-filter">Category:</label>
                      <select
                        id="category-filter"
                        value={selectedCategory}
                        onChange={(e) => onCategoryChange?.(e.target.value)}
                      >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-group">
                      <label htmlFor="priority-filter">Priority:</label>
                      <select
                        id="priority-filter"
                        value={selectedPriority}
                        onChange={(e) => onPriorityChange?.(e.target.value)}
                      >
                        <option value="all">All Priorities</option>
                        {priorities.map(priority => (
                          <option key={priority} value={priority}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-actions">
                      <button onClick={onResetFilters} className="reset-btn">
                        Reset Filters
                      </button>
                      <span className="filter-count">
                        Showing {filteredCount} of {totalCount} metrics
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            {/* Export Button Container */}
            {showExportButton && (
            <div className="export-button-container" ref={exportRef}>
              <motion.button 
                className={`action-button ${showExport ? 'active' : ''}`}
                onClick={() => setShowExport(!showExport)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Download size={18} />
                Export
              </motion.button>
              
              {showExport && (
                <motion.div 
                  className="header-dropdown export-panel"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="dropdown-header">
                    <h3>Export Data</h3>
                    <button 
                      className="close-btn"
                      onClick={() => setShowExport(false)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  
                  <div className="export-info">
                    <p>Export {filteredCount} {filteredCount === 1 ? 'record' : 'records'} for the selected date range</p>
                    <p className="date-range-info">
                      {dateRange.startDate && dateRange.endDate ? 
                        `${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}` :
                        'No date range selected'
                      }
                    </p>
                  </div>
                  
                  <div className="export-buttons">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onExport?.('csv');
                      }}
                      disabled={exportLoading}
                      className="export-btn csv"
                    >
                      {exportLoading ? 'Exporting...' : 'Export CSV'}
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onExport?.('excel');
                      }}
                      disabled={exportLoading}
                      className="export-btn excel"
                    >
                      {exportLoading ? 'Exporting...' : 'Export Excel'}
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onExport?.('pdf');
                      }}
                      disabled={exportLoading}
                      className="export-btn pdf"
                    >
                      {exportLoading ? 'Exporting...' : 'Export PDF'}
                    </button>
                  </div>
                  
                  {exportError && (
                    <div className="export-error">
                      <p>Export failed: {exportError}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
            )}
            
            {/* User Menu */}
            <div className="user-menu-container">
              <motion.button 
                className={`action-button user-button ${showUserMenu ? 'active' : ''}`}
                onClick={() => setShowUserMenu(!showUserMenu)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <User size={18} />
                {user?.name || 'User'}
              </motion.button>
              
              {showUserMenu && (
                <motion.div 
                  className="user-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className="user-info">
                    <div className="user-name">
                      {user?.name || 'User'}
                    </div>
                    <div className="user-email">{user?.email}</div>
                    <div className="user-role">{user?.role}</div>
                  </div>
                  <div className="user-actions">
                    <button
                      className="user-action-button"
                      onClick={() => {
                        navigate('/settings');
                        setShowUserMenu(false);
                      }}
                    >
                      <Settings size={16} />
                      Account Settings
                    </button>
                    {user?.role?.toLowerCase() === 'admin' && (
                      <button
                        className="user-action-button"
                        onClick={() => {
                          navigate('/admin/users');
                          setShowUserMenu(false);
                        }}
                      >
                        <User size={16} />
                        User Management
                      </button>
                    )}
                    <button
                      className="logout-button"
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Panels */}
      {showDateRange && (
        <motion.div 
          className="header-dropdown date-range-panel"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <div className="dropdown-header">
            <h3>Date Range</h3>
            <button 
              className="close-btn"
              onClick={() => setShowDateRange(false)}
            >
              <X size={16} />
            </button>
          </div>
          
          <div className="date-group">
            <label htmlFor="start-date">Start Date:</label>
            <input
              type="date"
              id="start-date"
              value={dateRange.startDate}
              onChange={(e) => onDateRangeChange?.({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          
          <div className="date-group">
            <label htmlFor="end-date">End Date:</label>
            <input
              type="date"
              id="end-date"
              value={dateRange.endDate}
              onChange={(e) => onDateRangeChange?.({ ...dateRange, endDate: e.target.value })}
            />
          </div>
          
          <div className="date-actions">
            <button 
              onClick={() => onDateRangeChange?.({
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              })}
              className="quick-date-btn"
            >
              Last 7 days
            </button>
            <button 
              onClick={() => onDateRangeChange?.({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              })}
              className="quick-date-btn"
            >
              Last 30 days
            </button>
            <button 
              onClick={() => onDateRangeChange?.({
                startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              })}
              className="quick-date-btn"
            >
              Last 90 days
            </button>
          </div>
        </motion.div>
      )}

    </motion.header>
  );
};

export default Header;
